import React, { useEffect } from "react";
import { FiX, FiAlertCircle } from "react-icons/fi";
import style from "./RejectionModal.module.css";

/**
 * Why a piece of content was rejected, shown when the creator opens it.
 *
 * Kept as an overlay rather than something inline on the content card: a
 * reviewer's reason can be long (or several points), and anything rendered in
 * the card grid would either be clipped or make one card taller than its
 * neighbours and tear a hole in the row. Here the body scrolls, so any length
 * works and the layout never shifts.
 *
 * props:
 *   open, reason, note, contentName, onClose, onEdit
 */
const RejectionModal = ({
  open,
  reason,
  note,
  contentName,
  onClose,
  onEdit,
}) => {
  // Escape closes, and the page behind must not scroll while this is up.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const hasDetail = Boolean(reason || note);

  return (
    <div className={style.overlay} onClick={onClose} role="presentation">
      <div
        className={style.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Content rejected"
      >
        <div className={style.header}>
          <div className={style.headTitle}>
            <span className={style.icon}>
              <FiAlertCircle size={18} />
            </span>
            <div>
              <h3 className={style.title}>This content was rejected</h3>
              {contentName && <p className={style.sub}>{contentName}</p>}
            </div>
          </div>
          <button
            type="button"
            className={style.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className={style.body}>
          {reason && (
            <div className={style.block}>
              <div className={style.label}>Reason</div>
              <div className={style.value}>{reason}</div>
            </div>
          )}
          {note && (
            <div className={style.block}>
              <div className={style.label}>Reviewer's note</div>
              <div className={style.value}>{note}</div>
            </div>
          )}
          {!hasDetail && (
            <div className={style.value}>
              No reason was recorded for this rejection. Please contact support
              if you need more detail.
            </div>
          )}
        </div>

        <div className={style.footer}>
          <p className={style.hint}>
            Update your content to address the feedback — it goes back for
            review once you save your changes.
          </p>
          <div className={style.actions}>
            <button
              type="button"
              className={style.ghostBtn}
              onClick={onClose}
            >
              Close
            </button>
            {onEdit && (
              <button
                type="button"
                className={style.primaryBtn}
                onClick={onEdit}
              >
                Edit content
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;
